/**
 * BankFeedContext — Phase 4 bank/CC statement registry + reconciliation actions.
 *
 * State: imported BankTxn[], one set of BankAccount mappings (cash + CC).
 * Writes are user-driven (CSV import, manual add, match, ignore). Matching
 * a txn posts a reclassifying JE through GeneralLedgerContext:
 *   bank inflow → DR Cash (real) / CR Unreconciled Cash
 *   bank outflow → DR Unreconciled Cash / CR Cash (rare in this clearing model)
 *   CC payment outflow → DR CC Payable / CR Cash
 *
 * No external bank feed yet; Plaid hooks in here later by appending BankTxn rows
 * with source: "plaid" and re-running the engine.
 */
import {
  createContext, useCallback, useContext, useMemo, useState,
} from "react";
import { useGeneralLedger } from "../GeneralLedgerContext";
import type { BankAccount, BankTxn, MatchSuggestion } from "./bankFeedTypes";
import { suggestMatches, summarize, type ReconciliationSummary } from "./reconciliationEngine";

/** Default mapped accounts. Edit later via Chart of Accounts UI. */
const DEFAULT_ACCOUNTS: BankAccount[] = [
  {
    glAccountId: "1000",
    label: "Operating Checking",
    kind: "bank",
    clearingAccountId: "1090",
  },
  {
    glAccountId: "1010",
    label: "Payroll Checking",
    kind: "bank",
    clearingAccountId: "1090",
  },
  {
    glAccountId: "2100",
    label: "Credit Card — Amex",
    kind: "credit_card",
    clearingAccountId: "2100",
  },
  {
    glAccountId: "2110",
    label: "Credit Card — Visa",
    kind: "credit_card",
    clearingAccountId: "2110",
  },
];

interface CsvRow {
  glAccountId: string;
  postedDate: string;
  description: string;
  amount: number;        // signed: + inflow, - outflow
  reference?: string;
}

interface BankFeedContextValue {
  accounts: BankAccount[];
  transactions: BankTxn[];
  getAccount: (glAccountId: string) => BankAccount | undefined;
  getSummary: (glAccountId: string) => ReconciliationSummary | null;
  getSuggestions: (txnId: string) => MatchSuggestion[];
  importRows: (rows: CsvRow[], source?: BankTxn["source"], by?: string) => number;
  importCsv: (csv: string, glAccountId: string, by?: string) => { imported: number; errors: string[] };
  addManual: (input: Omit<BankTxn, "id" | "status" | "importedAt" | "importedBy" | "source"> & { by?: string }) => BankTxn;
  matchToJournal: (txnId: string, journalEntryId: string, by?: string, note?: string) => void;
  /** Split a single bank txn across multiple posted JEs. Sum of clearing-leg
   *  amounts (on the expected side) across the selected JEs must equal the
   *  bank txn amount to the cent. */
  matchToMultiple: (txnId: string, journalEntryIds: string[], by?: string, note?: string) => void;
  ignore: (txnId: string, by?: string, note?: string) => void;
  resetMatch: (txnId: string, by?: string) => void;
}

const BankFeedContext = createContext<BankFeedContextValue | null>(null);

export function BankFeedProvider({ children }: { children: React.ReactNode }) {
  const gl = useGeneralLedger();
  const [accounts] = useState<BankAccount[]>(DEFAULT_ACCOUNTS);
  const [transactions, setTransactions] = useState<BankTxn[]>([]);

  const accountIndex = useMemo(() => {
    const m = new Map<string, BankAccount>();
    accounts.forEach(a => m.set(a.glAccountId, a));
    return m;
  }, [accounts]);

  const getAccount = useCallback((id: string) => accountIndex.get(id), [accountIndex]);

  const getSummary = useCallback((id: string) => {
    const a = accountIndex.get(id);
    if (!a) return null;
    return summarize(a, transactions);
  }, [accountIndex, transactions]);

  const getSuggestions = useCallback((txnId: string): MatchSuggestion[] => {
    const t = transactions.find(x => x.id === txnId);
    if (!t || t.status !== "unmatched") return [];
    const acct = accountIndex.get(t.glAccountId);
    if (!acct) return [];
    return suggestMatches(t, acct, gl.journalEntries);
  }, [transactions, accountIndex, gl.journalEntries]);

  const importRows = useCallback(
    (rows: CsvRow[], source: BankTxn["source"] = "csv", by = "Steve"): number => {
      if (rows.length === 0) return 0;
      const now = new Date().toISOString();
      const added: BankTxn[] = rows.map((r, i) => ({
        id: `bt_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 6)}`,
        glAccountId: r.glAccountId,
        postedDate: r.postedDate,
        description: r.description,
        amount: Math.abs(r.amount),
        side: r.amount >= 0 ? "inflow" : "outflow",
        reference: r.reference,
        status: "unmatched",
        importedAt: now,
        importedBy: by,
        source,
      }));
      setTransactions(prev => [...added, ...prev]);
      return added.length;
    },
    [],
  );

  const importCsv = useCallback(
    (csv: string, glAccountId: string, by = "Steve") => {
      const errors: string[] = [];
      const lines = csv.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      if (lines.length === 0) return { imported: 0, errors: ["Empty file"] };
      // Skip header if present
      const hasHeader = /date/i.test(lines[0]) && /amount/i.test(lines[0]);
      const dataLines = hasHeader ? lines.slice(1) : lines;
      const rows: CsvRow[] = [];
      dataLines.forEach((line, idx) => {
        const cols = line.split(",").map(c => c.trim().replace(/^"|"$/g, ""));
        // Expected: date, description, amount[, reference]
        if (cols.length < 3) {
          errors.push(`Row ${idx + 1}: needs at least date,description,amount`);
          return;
        }
        const [date, description, amountStr, reference] = cols;
        const amt = parseFloat(amountStr.replace(/[$,]/g, ""));
        if (!date || Number.isNaN(amt)) {
          errors.push(`Row ${idx + 1}: invalid date/amount`);
          return;
        }
        rows.push({
          glAccountId,
          postedDate: date,
          description,
          amount: amt,
          reference: reference || undefined,
        });
      });
      const imported = importRows(rows, "csv", by);
      return { imported, errors };
    },
    [importRows],
  );

  const addManual = useCallback<BankFeedContextValue["addManual"]>(
    (input) => {
      const now = new Date().toISOString();
      const txn: BankTxn = {
        id: `bt_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        glAccountId: input.glAccountId,
        postedDate: input.postedDate,
        description: input.description,
        amount: Math.abs(input.amount),
        side: input.side,
        reference: input.reference,
        status: "unmatched",
        importedAt: now,
        importedBy: input.by ?? "Steve",
        source: "manual",
      };
      setTransactions(prev => [txn, ...prev]);
      return txn;
    },
    [],
  );

  const matchToJournal = useCallback(
    (txnId: string, journalEntryId: string, by = "Steve", note?: string) => {
      const txn = transactions.find(t => t.id === txnId);
      if (!txn) throw new Error("Bank txn not found");
      if (txn.status !== "unmatched") throw new Error("Already resolved");
      const acct = accountIndex.get(txn.glAccountId);
      if (!acct) throw new Error("Unknown bank account mapping");
      const sourceJe = gl.journalEntries.find(j => j.id === journalEntryId);
      if (!sourceJe) throw new Error("Source JE not found");
      if (sourceJe.status !== "posted") throw new Error("Source JE is not posted");
      // Refuse to match into a closed period — the reclassifying JE would
      // either fail to post or (worse) silently sneak past the lock.
      if (gl.isPeriodClosed(sourceJe.period)) {
        throw new Error(`Source JE ${sourceJe.number} is in closed period ${sourceJe.period}.`);
      }
      const txnPeriod = txn.postedDate.slice(0, 7);
      if (gl.isPeriodClosed(txnPeriod)) {
        throw new Error(`Bank txn date (${txn.postedDate}) falls in closed period ${txnPeriod}.`);
      }
      // Guard: source JE must touch the clearing account on the opposite side of
      // the bank txn (an inflow on the bank statement = money debited to bank's
      // clearing intent, so the source JE should have a debit on the clearing
      // account; an outflow = credit). This prevents accidental cross-matching
      // between unrelated entries that happen to have the same amount.
      const expectedSide = txn.side === "inflow" ? "debit" : "credit";
      const matchAmtCents = Math.round(txn.amount * 100);
      const hasMatchingLeg = sourceJe.lines.some(l =>
        l.accountId === acct.clearingAccountId &&
        l.side === expectedSide &&
        Math.round(l.amount * 100) === matchAmtCents,
      );
      if (!hasMatchingLeg) {
        throw new Error(
          `Source JE ${sourceJe.number} has no ${expectedSide} of $${txn.amount.toFixed(2)} on the clearing account (${acct.clearingAccountId}).`,
        );
      }

      // Post the reclassifying entry only when the clearing account is distinct
      // from the bank account itself (cash clearing model). For CC where
      // clearing === payable, no reclass is needed; the match is just attestation.
      let reclassifyJeId: string | undefined;
      if (acct.clearingAccountId !== acct.glAccountId) {
        const reclassLines =
          txn.side === "inflow"
            ? [
                { accountId: acct.glAccountId, amount: txn.amount, side: "debit" as const,
                  memo: `Bank deposit cleared — ${txn.description}` },
                { accountId: acct.clearingAccountId, amount: txn.amount, side: "credit" as const,
                  memo: `Clear unreconciled (matches ${sourceJe.number})` },
              ]
            : [
                { accountId: acct.clearingAccountId, amount: txn.amount, side: "debit" as const,
                  memo: `Clear unreconciled (matches ${sourceJe.number})` },
                { accountId: acct.glAccountId, amount: txn.amount, side: "credit" as const,
                  memo: `Bank withdrawal cleared — ${txn.description}` },
              ];
        const reclassJe = gl.postJournal({
          postingDate: txn.postedDate,
          description: `Bank rec — ${txn.description}`,
          source: "bank_feed",
          sourceRef: { kind: "bank_match", id: txn.id },
          createdBy: by,
          lines: reclassLines,
        });
        reclassifyJeId = reclassJe.id;
      }

      const now = new Date().toISOString();
      setTransactions(prev => prev.map(t =>
        t.id === txnId
          ? { ...t, status: "matched", matchedJournalEntryId: journalEntryId,
              matchedReclassifyJeId: reclassifyJeId,
              matchedAt: now, matchedBy: by, matchNote: note }
          : t,
      ));
    },
    [transactions, accountIndex, gl],
  );

  const matchToMultiple = useCallback(
    (txnId: string, journalEntryIds: string[], by = "Steve", note?: string) => {
      const txn = transactions.find(t => t.id === txnId);
      if (!txn) throw new Error("Bank txn not found");
      if (txn.status !== "unmatched") throw new Error("Already resolved");
      if (journalEntryIds.length < 2) {
        throw new Error("Split match requires at least 2 journal entries. Use matchToJournal for single matches.");
      }
      const acct = accountIndex.get(txn.glAccountId);
      if (!acct) throw new Error("Unknown bank account mapping");
      const txnPeriod = txn.postedDate.slice(0, 7);
      if (gl.isPeriodClosed(txnPeriod)) {
        throw new Error(`Bank txn date (${txn.postedDate}) falls in closed period ${txnPeriod}.`);
      }

      const expectedSide = txn.side === "inflow" ? "debit" : "credit";
      const txnCents = Math.round(txn.amount * 100);

      // Validate every JE and sum the matching clearing-leg amounts.
      let sumCents = 0;
      const sourceJes = journalEntryIds.map(jid => {
        const je = gl.journalEntries.find(j => j.id === jid);
        if (!je) throw new Error(`Source JE ${jid} not found`);
        if (je.status !== "posted") throw new Error(`Source JE ${je.number} is not posted`);
        if (gl.isPeriodClosed(je.period)) {
          throw new Error(`Source JE ${je.number} is in closed period ${je.period}.`);
        }
        const legCents = je.lines
          .filter(l => l.accountId === acct.clearingAccountId && l.side === expectedSide)
          .reduce((s, l) => s + Math.round(l.amount * 100), 0);
        if (legCents === 0) {
          throw new Error(`Source JE ${je.number} has no ${expectedSide} on clearing account ${acct.clearingAccountId}.`);
        }
        sumCents += legCents;
        return je;
      });

      if (sumCents !== txnCents) {
        throw new Error(
          `Split mismatch: selected JEs total $${(sumCents / 100).toFixed(2)} on clearing leg but bank txn is $${txn.amount.toFixed(2)}.`,
        );
      }

      // Post a single reclassifying JE for the full txn amount (cash-clearing model).
      let reclassifyJeId: string | undefined;
      if (acct.clearingAccountId !== acct.glAccountId) {
        const refList = sourceJes.map(j => j.number).join(", ");
        const reclassLines =
          txn.side === "inflow"
            ? [
                { accountId: acct.glAccountId, amount: txn.amount, side: "debit" as const,
                  memo: `Bank deposit cleared — ${txn.description}` },
                { accountId: acct.clearingAccountId, amount: txn.amount, side: "credit" as const,
                  memo: `Clear unreconciled (split: ${refList})` },
              ]
            : [
                { accountId: acct.clearingAccountId, amount: txn.amount, side: "debit" as const,
                  memo: `Clear unreconciled (split: ${refList})` },
                { accountId: acct.glAccountId, amount: txn.amount, side: "credit" as const,
                  memo: `Bank withdrawal cleared — ${txn.description}` },
              ];
        const reclassJe = gl.postJournal({
          postingDate: txn.postedDate,
          description: `Bank rec (split) — ${txn.description}`,
          source: "bank_feed",
          sourceRef: { kind: "bank_match_split", id: txn.id },
          createdBy: by,
          lines: reclassLines,
        });
        reclassifyJeId = reclassJe.id;
      }

      const now = new Date().toISOString();
      setTransactions(prev => prev.map(t =>
        t.id === txnId
          ? { ...t, status: "matched",
              matchedJournalEntryId: journalEntryIds[0], // primary; full list in note
              matchedReclassifyJeId: reclassifyJeId,
              matchedAt: now, matchedBy: by,
              matchNote: note ?? `Split across ${journalEntryIds.length} JEs: ${sourceJes.map(j => j.number).join(", ")}` }
          : t,
      ));
    },
    [transactions, accountIndex, gl],
  );



  const ignore = useCallback((txnId: string, by = "Steve", note?: string) => {
    setTransactions(prev => prev.map(t =>
      t.id === txnId
        ? { ...t, status: "ignored", matchedAt: new Date().toISOString(),
            matchedBy: by, matchNote: note ?? "Ignored" }
        : t,
    ));
  }, []);

  const resetMatch = useCallback((txnId: string, by = "Steve") => {
    const txn = transactions.find(t => t.id === txnId);
    // Reverse the reclassifying JE (if any) so GL stays in sync with the bank-side reset.
    if (txn?.matchedReclassifyJeId) {
      try {
        gl.reverseJournal(txn.matchedReclassifyJeId, `Reset bank match for ${txn.description}`, by);
      } catch {
        // tolerate already-reversed JEs
      }
    }
    setTransactions(prev => prev.map(t =>
      t.id === txnId
        ? { ...t, status: "unmatched", matchedJournalEntryId: undefined,
            matchedReclassifyJeId: undefined,
            matchedAt: undefined, matchedBy: undefined, matchNote: undefined }
        : t,
    ));
  }, [transactions, gl]);

  const value = useMemo<BankFeedContextValue>(() => ({
    accounts, transactions, getAccount, getSummary, getSuggestions,
    importRows, importCsv, addManual, matchToJournal, matchToMultiple, ignore, resetMatch,
  }), [accounts, transactions, getAccount, getSummary, getSuggestions,
       importRows, importCsv, addManual, matchToJournal, matchToMultiple, ignore, resetMatch]);

  return <BankFeedContext.Provider value={value}>{children}</BankFeedContext.Provider>;
}

export function useBankFeed() {
  const ctx = useContext(BankFeedContext);
  if (!ctx) throw new Error("useBankFeed must be used inside <BankFeedProvider>");
  return ctx;
}
